/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import { handlerDetectionResult, handlerExecutionResult, launchCustomProtocol } from 'apmui/plugin/ProtocolHandler';
import { RequireOnly } from 'apmui/util/types';
import { UserAgent } from 'apmui/util/UserAgent';
import { ExpirableStatusCheck, IExpirableStatusCheckOptions } from './ExpirableStatusCheck';
import { PROGRESS_STATUS } from './Model';

interface ICustomProtocolHandlerOptions {
    browser: string;
    link: string;
    token: string;

    timeoutEnabled: boolean;
    pollingEnabled: boolean;
    pollingTimeout?: number;

    statusCheckOptions: RequireOnly<IExpirableStatusCheckOptions, 'onComplete' | 'onError' | 'onProgress' | 'uri'>;

    onAutoStartNotSupported(): void;
    onConnectionTimeout(): void;
    onNotInstalled(): void;
    onPopupBlockedIE(): void;
    onUserDeclined?(): void;

    onDebugMsg(msg: string): void;
    onHandlerDetected(): void;
    hideMessage(): void;
}

class CustomProtocolHandler {
    private options: ICustomProtocolHandlerOptions;
    private scOptions: IExpirableStatusCheckOptions;
    private cancelled: boolean;
    private clientAppIsAlive: boolean;
    private handlerDetected: boolean;
    private pollingTimer: number | null;
    private restartInterval: number;
    private sc: ExpirableStatusCheck | null;
    private attempt: number;

    private OnProgress: (eventName: string) => boolean;

    constructor(options: ICustomProtocolHandlerOptions) {
        this.options = options;
        this.OnProgress = this.options.statusCheckOptions.onProgress;

        // build statusCheckOptions - options for ExpirableStatusCheck
        this.scOptions = {
            ...this.options.statusCheckOptions,
            token: this.options.token,
            onDebugMsg: this.options.onDebugMsg,
            onProgress: (eventName) => {
                const res = this.OnProgress(eventName);
                // two first updates are always "obtaining_status" and "awaiting_connection"
                // ignoring them because they don't indicate connected client app
                if (
                    eventName !== PROGRESS_STATUS.obtaining_status &&
                    eventName !== PROGRESS_STATUS.awaiting_connection
                ) {
                    this.clientAppIsAlive = true;
                }
                if (res) {
                    this.HideMessage();
                }
                return res;
            },
            onTimeout: () => {
                if (this.options.timeoutEnabled) {
                    this.options.onConnectionTimeout();
                }
            },
        };
        this.clientAppIsAlive = false;

        this.handlerDetected = false;
        this.pollingTimer = null;
        this.sc = null;

        // set default values
        if (!this.options.pollingTimeout) {
            this.options.pollingTimeout = 5000;
        }
        this.cancelled = false;
    }

    Abort(): void {
        this.CallDebugCallback('Cancelling:' + this.options.token);
        this.cancelled = true;
        this.CancelDetection();
        this.HideMessage();
        if (this.sc) {
            this.sc.abort();
        }
    }

    CallDebugCallback(msg: string): void {
        if (this.options.onDebugMsg) {
            this.options.onDebugMsg(msg);
        }
    }

    HideMessage(): void {
        if (this.options.hideMessage) {
            this.options.hideMessage();
        }
    }

    RestartStatusCheckSoft(): void {
        if (this.sc) {
            this.sc.retryGetCheckStatus(1, true);
        }
    }

    AppendOtcToLink(otc: string): void {
        this.options.link += otc;
    }

    Run(userClick: boolean): boolean {
        this.CallDebugCallback('Starting:' + this.options.token);
        if (this.sc) {
            this.sc.abort();
        }
        this.sc = new ExpirableStatusCheck(this.scOptions);
        this.sc.run();

        // clear any other polling timeouts on user click
        if (userClick) {
            this.CancelDetection();
        }

        if (!userClick && UserAgent.MACEDGECLIENT !== this.options.browser) {
            this.options.onAutoStartNotSupported();
            return false;
        }

        console.log('launchCustomProtocol', this.options.link);
        launchCustomProtocol(
            this.options.link,
            this.OnLaunchResult.bind(this),
            this.OnPopupBlocked.bind(this),
            !userClick
        );
        return true;
    }

    private OnLaunchResult(
        url: string,
        userClick: boolean,
        detectionResult: handlerDetectionResult,
        executionResult: handlerExecutionResult,
        safePolling: boolean
    ): void {
        this.CallDebugCallback('handlerDetectionResult=' + detectionResult + ', executionResult=' + executionResult);
        if (this.cancelled) {
            return;
        }

        this.restartInterval = window.setInterval(() => this.connectionHealthCheck(), 1000);
        /* null check override: this.sc can't be null after Run() */
        if (this.sc!.completed) {
            // server already reported that client had connected
            this.handlerDetected = true;
        } else {
            this.handlerDetected = detectionResult === handlerDetectionResult.detected;
            if (executionResult === handlerExecutionResult.userDeclined) {
                return this.onUserDeclined();
            }
            if (this.handlerDetected) {
                /* null check override: this.sc can't be null after Run() */
                this.sc!.startTimeout();
            }
        }

        if (this.handlerDetected) {
            this.options.onHandlerDetected();
            return;
        }

        this.CallDebugCallback("<b><font color='red'>Protocol is not supported</font></b></br>");
        this.OnNotInstalled(userClick);

        if (safePolling && this.options.pollingEnabled) {
            this.pollingTimer = window.setTimeout(() => this.Run(userClick), this.options.pollingTimeout);
        }
    }

    private OnPopupBlocked(): void {
        this.options.onPopupBlockedIE();
    }

    private OnNotInstalled(userClick: boolean): void {
        this.CallDebugCallback('onNotInstalled' + (userClick ? ':userClick' : ''));
        this.options.onNotInstalled();
    }

    private onUserDeclined(): void {
        if (this.options.onUserDeclined) {
            this.options.onUserDeclined();
        }
    }

    private CancelDetection(): void {
        if (this.pollingTimer) {
            clearTimeout(this.pollingTimer);
            this.pollingTimer = null;
        }
    }

    private connectionHealthCheck(): void {
        if (!this.handlerDetected) {
            clearInterval(this.restartInterval);
            return;
        }
        // In some browsers (Chrome 61+) xml http requests are interruped when we
        // launch custom protocol handler by doing 'location.href = url'
        // so we need to restart status check xml http request manually
        if (!this.attempt) {
            // reseting 'attempt' to zero when calling function first time as
            // attempt is 'undefined' before first call. For all other cases we're
            // okay to reset it to zero if it's false, empty string or zero already
            this.attempt = 0;
        }
        /* null check override: this.sc can't be null after Run() */
        if (this.sc!.interruptedByBrowser()) {
            this.RestartStatusCheckSoft();
        } else if (this.clientAppIsAlive || this.attempt > 100) {
            clearInterval(this.restartInterval);
            this.attempt = 0;
            return;
        }
        this.attempt++;
    }
}

export { CustomProtocolHandler, ICustomProtocolHandlerOptions };
